import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Building, 
  FileText, 
  MessageSquare, 
  Plus,
  ArrowRight,
  Activity,
  FolderKanban,
  Truck,
  AlertTriangle,
  Thermometer,
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  Route,
  Cloud,
  MapPin,
  Bell,
  RefreshCw,
  ChevronRight,
  Wrench,
  DollarSign,
  ClipboardCheck,
  ShieldAlert,
  AlertOctagon,
  Navigation,
  ClipboardList,
  Fuel
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TECHLOG_API = process.env.REACT_APP_TECHLOG_API || 'https://techlog-1.emergent.host/api/public';
const FIELDMARSHAL_API = TECHLOG_API;

const CATEGORY_COLORS = {
  'Logistics': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Safety': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Financial': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  'Operations': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'HR': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  'Admin': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Project Management': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'IT': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  'Business Development': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'Engineering': 'bg-teal-500/20 text-teal-400 border-teal-500/30'
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({ departments: 0, documents: 0, queries: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Operations data
  const [activeTrips, setActiveTrips] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [pendingOverrides, setPendingOverrides] = useState([]);
  const [weatherData, setWeatherData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [whatsappLogsToday, setWhatsappLogsToday] = useState(0);
  
  // Titan GPS data
  const [titanMoving, setTitanMoving] = useState(0);
  const [titanTotal, setTitanTotal] = useState(0);
  
  // TechLog/Field Marshal data
  const [fieldMarshalProject, setFieldMarshalProject] = useState(null);
  const [fieldMarshalSummary, setFieldMarshalSummary] = useState(null);
  
  // FLHA Analytics data
  const [flhaStats, setFlhaStats] = useState(null);
  
  // Safety stats for all forms
  const [safetyStats, setSafetyStats] = useState(null);
  
  // Road Alerts data
  const [roadAlerts, setRoadAlerts] = useState({ ticker_messages: [], alerts: [], summary: {} });
  
  // Landmark Events data
  const [landmarkEvents, setLandmarkEvents] = useState({ events: [], summary: {}, active_landmarks: [] });
  
  // Fleetio Dashboard data
  const [fleetioData, setFleetioData] = useState({ vehicles: { summary: [] }, work_orders: { summary: [] } });
  
  // Dispatch data
  const [dispatchData, setDispatchData] = useState({ today: {}, tomorrow: {}, totals: {} });

  // Fuel spend data
  const [fuelSpend, setFuelSpend] = useState({ total: 0, volume: 0, fills: 0 });

  useEffect(() => {
    fetchDashboardData();
    // Refresh every 2 minutes
    const interval = setInterval(fetchDashboardData, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [
        departmentsRes, 
        tripsRes, 
        overridesRes, 
        weatherRes,
        logsRes,
        activityRes,
        titanRes
      ] = await Promise.all([
        axios.get(`${API}/departments`),
        axios.get(`${API}/trips/active`),
        axios.get(`${API}/override-requests`),
        axios.get(`${API}/weather`).catch(() => ({ data: { weather: [] } })),
        axios.get(`${API}/whatsapp/logs?limit=50`).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/recent-activity?limit=8`, { headers: authHeaders }).catch(() => ({ data: { activities: [] } })),
        axios.get(`${API}/titan-gps/vehicles`, { headers: authHeaders }).catch(() => ({ data: { success: false } }))
      ]);
      
      setDepartments(departmentsRes.data.slice(0, 4));
      setActiveTrips(tripsRes.data.trips || []);
      setOverdueCount(tripsRes.data.overdue_count || 0);
      
      // Titan GPS data
      if (titanRes.data.success) {
        setTitanMoving(titanRes.data.moving || 0);
        setTitanTotal(titanRes.data.count || 0);
      }
      
      // Filter pending overrides
      const allOverrides = overridesRes.data.requests || [];
      setPendingOverrides(allOverrides.filter(o => o.status === 'pending'));
      
      setWeatherData(weatherRes.data.weather || []);
      
      // Count today's WhatsApp logs
      const today = new Date().toISOString().split('T')[0];
      const todayLogs = (logsRes.data.logs || []).filter(log => 
        log.timestamp && log.timestamp.startsWith(today)
      );
      setWhatsappLogsToday(todayLogs.length);
      
      // Set recent activity from new endpoint
      setRecentActivity(activityRes.data.activities || []);
      
      const totalDocs = departmentsRes.data.reduce((sum, d) => sum + (d.document_count || 0), 0);
      setStats({
        departments: departmentsRes.data.length,
        documents: totalDocs,
        queries: logsRes.data.logs?.length || 0
      });
      
      // Fetch Field Marshal data (separate try-catch to not block other data)
      try {
        const projectsRes = await axios.get(`${FIELDMARSHAL_API}/projects`);
        const projects = projectsRes.data.projects || [];
        if (projects.length > 0) {
          const fullRes = await axios.get(`${FIELDMARSHAL_API}/projects/${projects[0].id}/full`);
          setFieldMarshalProject(fullRes.data.project);
          setFieldMarshalSummary(fullRes.data.summary);
        }
      } catch (fieldMarshalError) {
        console.error('Failed to fetch Field Marshal data:', fieldMarshalError);
      }
      
      // Fetch FLHA Analytics data
      try {
        const flhaRes = await axios.get(`${API}/forms/analytics`, { headers: authHeaders });
        setFlhaStats(flhaRes.data);
      } catch (flhaError) {
        console.error('Failed to fetch FLHA analytics:', flhaError);
      }
      
      // Fetch Safety Stats for all form types
      try {
        const safetyRes = await axios.get(`${API}/dashboard/safety-stats`, { headers: authHeaders });
        setSafetyStats(safetyRes.data);
      } catch (safetyError) {
        console.error('Failed to fetch safety stats:', safetyError);
      }
      
      // Fetch Road Alerts (Titan GPS + DriveBC cross-reference)
      try {
        const alertsRes = await axios.get(`${API}/fleet/road-alerts`, { headers: authHeaders });
        if (alertsRes.data.success) {
          setRoadAlerts(alertsRes.data);
        }
      } catch (alertsError) {
        console.error('Failed to fetch road alerts:', alertsError);
      }
      
      // Fetch Landmark Events (Titan GPS)
      try {
        const landmarkRes = await axios.get(`${API}/titan-gps/landmark-events?limit=20`, { headers: authHeaders });
        if (landmarkRes.data.success) {
          setLandmarkEvents(landmarkRes.data);
        }
      } catch (landmarkError) {
        console.error('Failed to fetch landmark events:', landmarkError);
      }
      
      // Fetch Fleetio Dashboard Data
      try {
        const fleetioRes = await axios.get(`${API}/fleetio/dashboard`, { headers: authHeaders });
        if (fleetioRes.data.success) {
          setFleetioData(fleetioRes.data);
        }
      } catch (fleetioError) {
        console.error('Failed to fetch Fleetio data:', fleetioError);
      }
      
      // Fetch Dispatch Dashboard Data
      try {
        const dispatchRes = await axios.get(`${API}/dispatch/dashboard`, { headers: authHeaders });
        if (dispatchRes.data.success) {
          setDispatchData(dispatchRes.data);
        }
      } catch (dispatchError) {
        console.error('Failed to fetch Dispatch data:', dispatchError);
      }

      // Fetch Fuel Spend (year-to-date)
      try {
        const yearStart = new Date().getFullYear() + '-01-01';
        const today = new Date().toISOString().split('T')[0];
        const fuelRes = await axios.get(`${API}/fuel/dashboard?start_date=${yearStart}&end_date=${today}`, { headers: authHeaders });
        if (fuelRes.data.success) {
          const s = fuelRes.data.summary;
          setFuelSpend({ total: s.total_spend || 0, volume: s.total_volume || 0, fills: s.total_purchases || 0 });
        }
      } catch (fuelError) {
        console.error('Failed to fetch fuel data:', fuelError);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const handleOverrideAction = async (requestId, action) => {
    try {
      await axios.post(`${API}/override-requests/${requestId}/${action}`, {
        response_note: `${action === 'approve' ? 'Approved' : 'Denied'} from dashboard`
      });
      toast.success(`Override ${action === 'approve' ? 'approved' : 'denied'}`);
      fetchDashboardData();
    } catch (error) {
      toast.error(`Failed to ${action} override`);
    }
  };

  const endTrip = async (tripId) => {
    try {
      await axios.put(`${API}/trips/${tripId}/end`);
      toast.success('Trip ended');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to end trip');
    }
  };

  // Count weather alerts
  const weatherAlerts = weatherData.filter(w => 
    w.status === 'critical' || w.status === 'warning'
  ).length;

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Operations overview for Rogers OPS
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Link to="/dashboard/departments">
            <Button className="btn-glow">
              <Plus className="w-4 h-4 mr-2" />
              New Department
            </Button>
          </Link>
        </div>
      </div>

      {/* Road Alerts Ticker */}
      {roadAlerts.ticker_messages?.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-red-950/50 via-red-900/30 to-red-950/50 border border-red-500/30 rounded-lg" data-testid="road-alerts-ticker">
          <div className="flex items-center">
            {/* Alert Icon */}
            <div className="flex-shrink-0 bg-red-600/20 px-4 py-3 flex items-center gap-2 border-r border-red-500/30">
              <AlertOctagon className="w-5 h-5 text-red-400 animate-pulse" />
              <span className="text-red-400 font-semibold text-sm hidden sm:block">ROAD ALERTS</span>
              <span className="text-red-400 font-bold">{roadAlerts.summary?.total_alerts || 0}</span>
            </div>
            
            {/* Scrolling Ticker */}
            <div className="flex-1 overflow-hidden py-3">
              <div className="animate-ticker flex whitespace-nowrap">
                {[...roadAlerts.ticker_messages, ...roadAlerts.ticker_messages].map((alert, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center mx-8 text-red-300"
                  >
                    <span className="mr-2">🚨</span>
                    {alert.message}
                    <span className="mx-4 text-red-500/50">•</span>
                  </span>
                ))}
              </div>
            </div>
            
            {/* View All Link */}
            <Link to="/dashboard/titan-gps" className="flex-shrink-0 px-4 py-3 border-l border-red-500/30 hover:bg-red-500/10 transition-colors">
              <span className="text-red-400 text-sm flex items-center gap-1">
                View <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      )}
      
      {/* Road Closures Ticker (scrolling amber banner when no immediate truck threats) */}
      {roadAlerts.ticker_messages?.length === 0 && roadAlerts.closure_alerts?.length > 0 && (
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-950/50 via-amber-900/30 to-amber-950/50 border border-amber-500/30 rounded-lg" data-testid="road-closures-ticker">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-amber-600/20 px-4 py-3 flex items-center gap-2 border-r border-amber-500/30">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-400 font-semibold text-sm hidden sm:block">CLOSURES</span>
              <span className="text-amber-400 font-bold">{roadAlerts.closure_alerts?.length || 0}</span>
            </div>
            
            <div className="flex-1 overflow-hidden py-3">
              <div className="animate-ticker flex whitespace-nowrap">
                {[...roadAlerts.closure_alerts, ...roadAlerts.closure_alerts].map((closure, idx) => (
                  <span key={idx} className="inline-flex items-center mx-8 text-amber-300">
                    {closure.message}
                    <span className="mx-4 text-amber-500/50">•</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Operations Overview - Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Trips (WhatsApp check-ins) */}
        <Link to="/dashboard/whatsapp">
          <Card className={`card-hover cursor-pointer h-full ${overdueCount > 0 ? 'border-amber-500/50 bg-amber-500/5' : activeTrips.length > 0 ? 'border-emerald-500/30' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Active Trips</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${overdueCount > 0 ? 'text-amber-400' : activeTrips.length > 0 ? 'text-emerald-400' : 'text-foreground'}`}>
                    {activeTrips.length}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${overdueCount > 0 ? 'bg-amber-500/10' : activeTrips.length > 0 ? 'bg-emerald-500/10' : 'bg-secondary'}`}>
                  <Route className={`w-5 h-5 lg:w-6 lg:h-6 ${overdueCount > 0 ? 'text-amber-400' : activeTrips.length > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${overdueCount > 0 ? 'text-amber-400 font-medium' : activeTrips.length > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                {overdueCount > 0 ? `⚠️ ${overdueCount} overdue - check in!` : activeTrips.length > 0 ? 'Reported via WhatsApp' : 'No trips reported'}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Vehicles Moving (Titan GPS) */}
        <Link to="/dashboard/titan-gps">
          <Card className={`card-hover cursor-pointer h-full ${titanMoving > 0 ? 'border-green-500/30 bg-green-500/5' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Vehicles Moving</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${titanMoving > 0 ? 'text-green-400' : 'text-foreground'}`}>
                    {titanMoving}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${titanMoving > 0 ? 'bg-green-500/10' : 'bg-secondary'}`}>
                  <Truck className={`w-5 h-5 lg:w-6 lg:h-6 ${titanMoving > 0 ? 'text-green-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${titanMoving > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                {titanMoving > 0 ? `${titanTotal} total fleet via GPS` : `${titanTotal} vehicles tracked`}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Pending Overrides */}
        <Link to="/dashboard/whatsapp">
          <Card className={`card-hover cursor-pointer h-full ${pendingOverrides.length > 0 ? 'border-red-500/50 bg-red-500/5' : ''}`}>

        {/* Fuel Spend YTD */}
        <Link to="/dashboard/fuel">
          <Card className="card-hover cursor-pointer h-full border-amber-500/30 bg-amber-500/5" data-testid="fuel-spend-card">
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Fuel Spend YTD</p>
                  <p className="text-2xl lg:text-3xl font-bold mt-1 text-amber-400">
                    ${fuelSpend.total > 999 ? `${(fuelSpend.total / 1000).toFixed(1)}k` : fuelSpend.total.toFixed(0)}
                  </p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Fuel className="w-5 h-5 lg:w-6 lg:h-6 text-amber-400" />
                </div>
              </div>
              <p className="text-xs mt-2 text-amber-400">
                {fuelSpend.fills} fills | {(fuelSpend.volume / 1000).toFixed(1)}k litres
              </p>
            </CardContent>
          </Card>
        </Link>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Pending Overrides</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${pendingOverrides.length > 0 ? 'text-red-400' : 'text-foreground'}`}>
                    {pendingOverrides.length}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${pendingOverrides.length > 0 ? 'bg-red-500/10' : 'bg-secondary'}`}>
                  <AlertTriangle className={`w-5 h-5 lg:w-6 lg:h-6 ${pendingOverrides.length > 0 ? 'text-red-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${pendingOverrides.length > 0 ? 'text-red-400 font-medium' : 'text-muted-foreground'}`}>
                {pendingOverrides.length > 0 ? 'Needs attention!' : 'All clear'}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Weather Alerts */}
        <Link to="/dashboard/weather">
          <Card className={`card-hover cursor-pointer h-full ${weatherAlerts > 0 ? 'border-orange-500/30' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Weather Alerts</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${weatherAlerts > 0 ? 'text-orange-400' : 'text-foreground'}`}>
                    {weatherAlerts}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${weatherAlerts > 0 ? 'bg-orange-500/10' : 'bg-secondary'}`}>
                  <Thermometer className={`w-5 h-5 lg:w-6 lg:h-6 ${weatherAlerts > 0 ? 'text-orange-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${weatherAlerts > 0 ? 'text-orange-400' : 'text-muted-foreground'}`}>
                {weatherAlerts > 0 ? 'Critical/Warning' : 'Conditions normal'}
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* WhatsApp Queries Today */}
        <Link to="/dashboard/whatsapp">
          <Card className="card-hover cursor-pointer h-full">
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">WhatsApp Today</p>
                  <p className="text-2xl lg:text-3xl font-bold text-foreground mt-1">{whatsappLogsToday}</p>
                </div>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Phone className="w-5 h-5 lg:w-6 lg:h-6 text-purple-400" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Driver queries</p>
            </CardContent>
          </Card>
        </Link>

        {/* FLHA Today */}
        <Link to="/dashboard/forms/analytics">
          <Card className={`card-hover cursor-pointer h-full ${flhaStats?.summary?.today > 0 ? 'border-emerald-500/30 bg-emerald-500/5' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">FLHAs Today</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${flhaStats?.summary?.today > 0 ? 'text-emerald-400' : 'text-foreground'}`}>
                    {flhaStats?.summary?.today || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${flhaStats?.summary?.today > 0 ? 'bg-emerald-500/10' : 'bg-secondary'}`}>
                  <ClipboardCheck className={`w-5 h-5 lg:w-6 lg:h-6 ${flhaStats?.summary?.today > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${flhaStats?.summary?.today > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                {flhaStats?.summary?.total?.toLocaleString() || 0} total • {flhaStats?.summary?.this_week || 0} this week
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Hazard IDs Today */}
        <Link to="/dashboard/forms/analytics/hazard_id">
          <Card className={`card-hover cursor-pointer h-full ${safetyStats?.hazard_id?.today > 0 ? 'border-amber-500/30 bg-amber-500/5' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Hazards Today</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${safetyStats?.hazard_id?.today > 0 ? 'text-amber-400' : 'text-foreground'}`}>
                    {safetyStats?.hazard_id?.today || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${safetyStats?.hazard_id?.today > 0 ? 'bg-amber-500/10' : 'bg-secondary'}`}>
                  <ShieldAlert className={`w-5 h-5 lg:w-6 lg:h-6 ${safetyStats?.hazard_id?.today > 0 ? 'text-amber-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${safetyStats?.hazard_id?.today > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                {safetyStats?.hazard_id?.total?.toLocaleString() || 0} total • {safetyStats?.hazard_id?.this_week || 0} this week
              </p>
            </CardContent>
          </Card>
        </Link>

        {/* Incidents Today */}
        <Link to="/dashboard/forms/analytics/incident">
          <Card className={`card-hover cursor-pointer h-full ${safetyStats?.incident?.today > 0 ? 'border-red-500/30 bg-red-500/5' : ''}`}>
            <CardContent className="p-4 lg:p-6 h-[120px] flex flex-col justify-between">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm text-muted-foreground">Incidents Today</p>
                  <p className={`text-2xl lg:text-3xl font-bold mt-1 ${safetyStats?.incident?.today > 0 ? 'text-red-400' : 'text-foreground'}`}>
                    {safetyStats?.incident?.today || 0}
                  </p>
                </div>
                <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-lg flex items-center justify-center ${safetyStats?.incident?.today > 0 ? 'bg-red-500/10' : 'bg-secondary'}`}>
                  <AlertOctagon className={`w-5 h-5 lg:w-6 lg:h-6 ${safetyStats?.incident?.today > 0 ? 'text-red-400' : 'text-muted-foreground'}`} />
                </div>
              </div>
              <p className={`text-xs mt-2 ${safetyStats?.incident?.today > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                {safetyStats?.incident?.total?.toLocaleString() || 0} total • {safetyStats?.incident?.this_week || 0} this week
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Active Trips & Pending Overrides Row */}
      {(activeTrips.length > 0 || pendingOverrides.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Active Trips Panel */}
          {activeTrips.length > 0 && (
            <Card className="border-emerald-500/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Truck className="w-5 h-5 text-emerald-400" />
                    Drivers On The Road
                  </CardTitle>
                  <Link to="/dashboard/whatsapp" className="text-xs text-primary hover:underline">
                    View all
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {activeTrips.slice(0, 3).map((trip) => {
                  const traffic = trip.last_check?.traffic || trip.initial_conditions?.traffic;
                  const startedAt = new Date(trip.started_at);
                  const drivingMinutes = Math.round((new Date() - startedAt) / 60000);
                  const estimatedMinutes = trip.estimated_duration_minutes || 0;
                  const isOverdue = trip.is_overdue || (estimatedMinutes > 0 && drivingMinutes > estimatedMinutes + 30);
                  const overdueBy = trip.overdue_by_minutes || (isOverdue ? drivingMinutes - estimatedMinutes : 0);
                  
                  return (
                    <div 
                      key={trip.id}
                      className={`p-3 rounded-lg border ${isOverdue ? 'bg-amber-500/10 border-amber-500/30' : 'bg-secondary/30 border-border/50'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Route className="w-4 h-4 text-primary flex-shrink-0" />
                            <span className="truncate">{trip.origin} → {trip.destination}</span>
                            {isOverdue && (
                              <Badge variant="outline" className="text-xs bg-amber-500/20 text-amber-400 border-amber-500/30 animate-pulse">
                                ⚠️ +{overdueBy}min overdue
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {trip.driver_phone}
                            </span>
                            <span className={`flex items-center gap-1 ${isOverdue ? 'text-amber-400' : ''}`}>
                              <Clock className="w-3 h-3" />
                              Driving: {drivingMinutes < 60 ? `${drivingMinutes} min` : `${Math.floor(drivingMinutes/60)}h ${drivingMinutes%60}m`}
                              {estimatedMinutes > 0 && !isOverdue && ` / ${estimatedMinutes < 60 ? `${estimatedMinutes}min` : `${Math.floor(estimatedMinutes/60)}h ${estimatedMinutes%60}m`} est`}
                            </span>
                            {traffic && (
                              <span className="flex items-center gap-1">
                                {traffic.traffic_emoji} {traffic.formatted_time}
                              </span>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant={isOverdue ? "default" : "ghost"}
                          className={`text-xs h-7 ${isOverdue ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                          onClick={() => endTrip(trip.id)}
                        >
                          {isOverdue ? 'End Trip' : 'End'}
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {activeTrips.length > 3 && (
                  <Link to="/dashboard/whatsapp" className="block text-center text-xs text-primary hover:underline py-2">
                    +{activeTrips.length - 3} more trips
                  </Link>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pending Overrides Panel */}
          {pendingOverrides.length > 0 && (
            <Card className="border-red-500/30 bg-red-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Override Requests Pending
                  </CardTitle>
                  <Badge variant="destructive" className="text-xs">
                    {pendingOverrides.length} waiting
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingOverrides.slice(0, 3).map((override) => (
                  <div 
                    key={override.id}
                    className="p-3 rounded-lg bg-background/50 border border-red-500/20"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">
                          {override.route_from} → {override.route_to}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {override.reason}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Driver: {override.driver_phone}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                          onClick={() => handleOverrideAction(override.id, 'approve')}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs text-red-400 border-red-500/30 hover:bg-red-500/10"
                          onClick={() => handleOverrideAction(override.id, 'deny')}
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Current Conditions & Quick Stats */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Weather Summary */}
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Cloud className="w-5 h-5 text-blue-400" />
                Current Weather
              </CardTitle>
              <Link to="/dashboard/weather" className="text-xs text-primary hover:underline">
                Details
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {weatherData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Loading weather...</p>
            ) : (
              <div className="space-y-2">
                {weatherData.slice(0, 4).map((location) => (
                  <div 
                    key={location.location?.id || location.location?.name}
                    className="flex items-center justify-between p-2 rounded bg-secondary/30"
                  >
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3 h-3 text-muted-foreground" />
                      <span className="text-sm">{location.location?.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium ${
                        location.status === 'critical' ? 'text-red-400' :
                        location.status === 'warning' ? 'text-orange-400' :
                        location.status === 'caution' ? 'text-yellow-400' :
                        'text-emerald-400'
                      }`}>
                        {location.weather?.temp ? `${Math.round(location.weather.temp)}°C` : 'N/A'}
                      </span>
                      {location.status !== 'safe' && (
                        <div className={`w-2 h-2 rounded-full ${
                          location.status === 'critical' ? 'bg-red-400' :
                          location.status === 'warning' ? 'bg-orange-400' :
                          'bg-yellow-400'
                        }`} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card className="h-full">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              System Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-primary" />
                  <span className="text-sm">Departments</span>
                </div>
                <span className="text-sm font-bold">{stats.departments}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <span className="text-sm">Documents</span>
                </div>
                <span className="text-sm font-bold">{stats.documents}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-purple-400" />
                  <span className="text-sm">Total Queries</span>
                </div>
                <span className="text-sm font-bold">{stats.queries}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="h-full">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-400" />
                Recent Activity
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
            ) : (
              <div className="space-y-2">
                {recentActivity.slice(0, 6).map((activity, idx) => {
                  const getIcon = () => {
                    switch(activity.icon) {
                      case 'clipboard': return <ClipboardList className="w-3.5 h-3.5" />;
                      case 'truck': return <Truck className="w-3.5 h-3.5" />;
                      case 'check': return <CheckCircle className="w-3.5 h-3.5" />;
                      case 'x': return <XCircle className="w-3.5 h-3.5" />;
                      case 'alert': return <AlertTriangle className="w-3.5 h-3.5" />;
                      case 'message': return <MessageSquare className="w-3.5 h-3.5" />;
                      default: return <Activity className="w-3.5 h-3.5" />;
                    }
                  };
                  
                  const colorClass = {
                    emerald: 'text-emerald-400 bg-emerald-500/10',
                    red: 'text-red-400 bg-red-500/10',
                    amber: 'text-amber-400 bg-amber-500/10',
                    blue: 'text-blue-400 bg-blue-500/10',
                    green: 'text-green-400 bg-green-500/10',
                    purple: 'text-purple-400 bg-purple-500/10'
                  }[activity.color] || 'text-gray-400 bg-gray-500/10';
                  
                  return (
                    <Link 
                      key={idx}
                      to={activity.link || '#'}
                      className="flex items-start gap-2 p-2 rounded bg-secondary/30 hover:bg-secondary/50 transition-colors text-xs group"
                    >
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        {getIcon()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground truncate group-hover:text-primary transition-colors">
                          {activity.title}
                        </p>
                        {activity.subtitle && (
                          <p className="text-muted-foreground truncate text-[10px]">
                            {activity.subtitle}
                          </p>
                        )}
                      </div>
                      <span className="text-muted-foreground flex-shrink-0 text-[10px]">
                        {formatTimeAgo(activity.timestamp)}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fleetio Fleet Status Widget */}
      {(fleetioData.vehicles?.summary?.length > 0 || fleetioData.work_orders?.summary?.length > 0) && (
        <div className="mt-6">
          <Link to="/dashboard/fleetio" data-testid="fleetio-widget">
            <Card className="card-hover cursor-pointer border-blue-500/20 bg-blue-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-400" />
                    Fleet Status
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                      {fleetioData.vehicles?.total || 0} vehicles
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {/* Vehicle Status */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Vehicle Status</p>
                    <div className="flex flex-wrap gap-1">
                      {fleetioData.vehicles?.summary?.slice(0, 4).map((s, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            s.color === 'green' ? 'bg-green-500/20 text-green-400' :
                            s.color === 'red' ? 'bg-red-500/20 text-red-400' :
                            s.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                            s.color === 'blue' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {s.status}: {s.count}
                        </span>
                      ))}
                    </div>
                  </div>
                  {/* Work Orders */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Work Orders</p>
                    <div className="flex flex-wrap gap-1">
                      {fleetioData.work_orders?.summary?.map((s, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            s.color === 'teal' ? 'bg-teal-500/20 text-teal-400' :
                            s.color === 'orange' ? 'bg-orange-500/20 text-orange-400' :
                            s.color === 'olive' ? 'bg-lime-500/20 text-lime-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}
                        >
                          {s.status}: {s.count}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Landmark Events Widget */}
      {landmarkEvents.events?.length > 0 && (
        <div className="mt-6">
          <Link to="/dashboard/titan-gps" data-testid="landmark-events-widget">
            <Card className="card-hover cursor-pointer border-green-500/20 bg-green-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Landmark Activity
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                      {landmarkEvents.summary?.entries || 0} arrivals
                    </Badge>
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/30">
                      {landmarkEvents.summary?.exits || 0} departures
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {landmarkEvents.events.slice(0, 5).map((event, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded ${
                        event.type === 'entry' 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : 'bg-amber-500/20 border border-amber-500/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-lg ${event.type === 'entry' ? 'text-green-400' : 'text-amber-400'}`}>
                          {event.type === 'entry' ? '📍' : '🚚'}
                        </span>
                        <div>
                          <span className={`text-sm font-bold ${event.type === 'entry' ? 'text-green-400' : 'text-amber-400'}`}>
                            {event.device_name}
                            {event.unit_number && event.unit_number !== event.device_name && ` (${event.unit_number})`}
                          </span>
                          <span className="text-sm text-slate-300 ml-2">
                            {event.type === 'entry' ? 'arrived at' : 'left'}
                          </span>
                          <span className="text-sm text-white ml-1 font-medium">
                            {event.landmark_name}
                          </span>
                          {event.previous_landmark && (
                            <span className="text-sm text-slate-400 ml-1">
                              (from {event.previous_landmark})
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(event.created_at)}
                      </span>
                    </div>
                  ))}
                </div>
                {landmarkEvents.active_landmarks?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <p className="text-xs text-muted-foreground mb-2">Active Locations</p>
                    <div className="flex flex-wrap gap-2">
                      {landmarkEvents.active_landmarks.slice(0, 5).map((landmark, idx) => (
                        <Badge 
                          key={idx}
                          variant="outline" 
                          className="text-xs bg-secondary/50"
                        >
                          <MapPin className="w-3 h-3 mr-1" />
                          {landmark.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Dispatch Management Widget */}
      {(dispatchData.today?.assignments > 0 || dispatchData.tomorrow?.assignments > 0 || dispatchData.totals?.jobs > 0) && (
        <div className="mt-6">
          <Link to="/dashboard/dispatch" data-testid="dispatch-widget">
            <Card className="card-hover cursor-pointer border-purple-500/20 bg-purple-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-purple-400" />
                    Dispatch Board
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {dispatchData.today?.active_trips > 0 && (
                      <Badge variant="outline" className="text-xs bg-green-500/10 text-green-400 border-green-500/30">
                        {dispatchData.today.active_trips} active
                      </Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-purple-400">{dispatchData.today?.assignments || 0}</p>
                    <p className="text-xs text-muted-foreground">Today</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-blue-400">{dispatchData.tomorrow?.assignments || 0}</p>
                    <p className="text-xs text-muted-foreground">Tomorrow</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{dispatchData.totals?.jobs || 0}</p>
                    <p className="text-xs text-muted-foreground">Jobs</p>
                  </div>
                  <div className="text-center p-3 bg-secondary/30 rounded-lg">
                    <p className="text-2xl font-bold text-foreground">{dispatchData.totals?.drivers || 0}</p>
                    <p className="text-xs text-muted-foreground">Drivers</p>
                  </div>
                </div>
                
                {/* Tomorrow's Dispatch Preview */}
                {dispatchData.tomorrow?.details?.length > 0 && (
                  <div className="border-t border-border pt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-2">TOMORROW'S DISPATCH</p>
                    <div className="space-y-2">
                      {dispatchData.tomorrow.details.slice(0, 5).map((assignment, idx) => {
                        const needsAttention = !assignment.status || assignment.status === 'NOT_ASSIGNED' || assignment.status === 'PRELOAD';
                        return (
                          <div key={assignment.id || idx} className={`flex items-center justify-between text-sm p-2 rounded ${needsAttention ? 'bg-amber-500/20 border border-amber-500/30' : 'bg-secondary/20'}`}>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-blue-400">#{assignment.job_number}</span>
                              <span className="text-muted-foreground">{assignment.client_name}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                              {needsAttention && (
                                <span className="text-amber-400 font-medium">⚠ Not Confirmed</span>
                              )}
                              {assignment.driver_name && <span className="text-muted-foreground">{assignment.driver_name}</span>}
                              {assignment.unit_name && <span className="text-purple-400">{assignment.unit_name}</span>}
                            </div>
                          </div>
                        );
                      })}
                      {dispatchData.tomorrow.details.length > 5 && (
                        <p className="text-xs text-muted-foreground text-center">
                          +{dispatchData.tomorrow.details.length - 5} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Field Marshal Project Widget */}
      {fieldMarshalProject && (
        <div className="mt-6">
          <Link to="/dashboard/fieldmarshal" data-testid="fieldmarshal-widget">
            <Card className="card-hover cursor-pointer border-cyan-500/20 bg-cyan-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-cyan-400" />
                    Field Marshal
                  </CardTitle>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-foreground">{fieldMarshalProject.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">{fieldMarshalProject.description}</p>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div>
                      <p className="text-xs text-muted-foreground">Hours</p>
                      <p className="text-lg font-bold text-blue-400">
                        {fieldMarshalSummary?.total_hours?.toFixed(1) || '0'}h
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Labor Cost</p>
                      <p className="text-lg font-bold text-emerald-400">
                        ${fieldMarshalSummary?.total_labor_cost?.toLocaleString() || '0'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Material</p>
                      <p className="text-lg font-bold text-amber-400">
                        ${fieldMarshalSummary?.total_material_cost?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* FLHA Safety Insights Widget */}
      {flhaStats && flhaStats.summary?.total > 0 && (
        <div className="mt-6">
          <Link to="/dashboard/forms/analytics" data-testid="flha-analytics-widget">
            <Card className="card-hover cursor-pointer border-red-500/20 bg-red-500/5">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    Safety Forms (FLHA)
                  </CardTitle>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold text-blue-400">{flhaStats.summary.total.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total FLHAs</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold text-emerald-400">{flhaStats.summary.today}</p>
                    <p className="text-xs text-muted-foreground">Today</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold text-purple-400">{flhaStats.summary.this_week}</p>
                    <p className="text-xs text-muted-foreground">This Week</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-secondary/30">
                    <p className="text-2xl font-bold text-cyan-400">{flhaStats.summary.unique_workers}</p>
                    <p className="text-xs text-muted-foreground">Workers</p>
                  </div>
                </div>
                
                {/* Top Hazards */}
                {flhaStats.top_hazards?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Top Hazards Identified
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {flhaStats.top_hazards.slice(0, 5).map((hazard) => (
                        <span 
                          key={hazard.id}
                          className="px-2 py-1 text-xs rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/20"
                        >
                          {hazard.label} ({hazard.count.toLocaleString()})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        </div>
      )}

      {/* Hazard ID, Incident, and Site Tour Insights - Grid */}
      {safetyStats && (safetyStats.hazard_id?.total > 0 || safetyStats.incident?.total > 0 || safetyStats.site_tour?.total > 0) && (
        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          {/* Hazard ID Widget */}
          {safetyStats.hazard_id?.total > 0 && (
            <Link to="/dashboard/forms/analytics/hazard_id" data-testid="hazard-analytics-widget">
              <Card className="card-hover cursor-pointer h-full border-amber-500/20 bg-amber-500/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      Hazard Identification
                    </CardTitle>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-amber-400">{safetyStats.hazard_id.total.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">Total</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-emerald-400">{safetyStats.hazard_id.today}</p>
                      <p className="text-[10px] text-muted-foreground">Today</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-purple-400">{safetyStats.hazard_id.this_week}</p>
                      <p className="text-[10px] text-muted-foreground">This Week</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-400 text-center">Click for detailed analytics →</p>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Incident Widget */}
          {safetyStats.incident?.total > 0 && (
            <Link to="/dashboard/forms/analytics/incident" data-testid="incident-analytics-widget">
              <Card className="card-hover cursor-pointer h-full border-red-500/20 bg-red-500/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <AlertOctagon className="w-4 h-4 text-red-400" />
                      Incident Reports
                    </CardTitle>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-red-400">{safetyStats.incident.total.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">Total</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-emerald-400">{safetyStats.incident.today}</p>
                      <p className="text-[10px] text-muted-foreground">Today</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-purple-400">{safetyStats.incident.this_week}</p>
                      <p className="text-[10px] text-muted-foreground">This Week</p>
                    </div>
                  </div>
                  <p className="text-xs text-red-400 text-center">Click for detailed analytics →</p>
                </CardContent>
              </Card>
            </Link>
          )}

          {/* Site Tour Widget */}
          {safetyStats.site_tour?.total > 0 && (
            <Link to="/dashboard/forms/analytics/site_tour" data-testid="site-tour-analytics-widget">
              <Card className="card-hover cursor-pointer h-full border-blue-500/20 bg-blue-500/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-blue-400" />
                      Site Tours
                    </CardTitle>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-blue-400">{safetyStats.site_tour.total.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">Total</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-emerald-400">{safetyStats.site_tour.today}</p>
                      <p className="text-[10px] text-muted-foreground">Today</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-secondary/30">
                      <p className="text-lg font-bold text-purple-400">{safetyStats.site_tour.this_week}</p>
                      <p className="text-[10px] text-muted-foreground">This Week</p>
                    </div>
                  </div>
                  <p className="text-xs text-blue-400 text-center">Click for detailed analytics →</p>
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
